<script setup>
import { computed } from 'vue';
import { compareRoadmaps } from '@/lib/overlay';
import { formatRange } from '@/lib/schema';
import { formatDay, slotToDate } from '@/lib/dates';

const props = defineProps({
  roadmaps: { type: Array, required: true },
  compact: { type: Boolean, default: false }
});

const result = computed(() => compareRoadmaps(props.roadmaps));

function runs(values, keyOf) {
  const out = [];
  let i = 0;
  while (i < values.length) {
    const k = keyOf(values[i]);
    if (k === null) {
      i++;
      continue;
    }
    let j = i + 1;
    while (j < values.length && keyOf(values[j]) === k) j++;
    out.push({ start: i, span: j - i, key: k, value: values[i] });
    i = j;
  }
  return out;
}

const laneRuns = computed(() => result.value.lanes.map((l) => runs(l.busy, (n) => (n > 0 ? 'on' : null))));
const combined = computed(() => runs(result.value.combined, (n) => (n === 0 ? 'gap' : n === 1 ? 'one' : 'many')));

// Roadmaps with start dates are lined up by calendar, so label the shared axis with dates.
const dated = computed(() => result.value.alignment?.mode === 'dates');

function sprintLabel(n) {
  return dated.value ? formatDay(slotToDate(result.value.alignment.base, (n - 1) * 2)) : `S${n}`;
}

function rangeLabel(start, span) {
  if (!dated.value) return formatRange(start, span);
  const base = result.value.alignment.base;
  const to = new Date(slotToDate(base, start + span).getTime() - 864e5);
  return `${formatDay(slotToDate(base, start))} to ${formatDay(to)}`;
}

const clashText = computed(() =>
  result.value.clashes.map((c) => {
    const ranges = runs(c.perSlot, (x) => (x.length ? 'c' : null)).map((r) => rangeLabel(r.start, r.span));
    return `${c.name} is on both roadmaps in ${ranges.join(' and ')}`;
  })
);

const pos = (run, row) => ({ gridColumn: `${run.start + 2} / span ${run.span}`, gridRow: row });
</script>

<template>
  <div class="coverage" :class="{ compact }">
    <div class="grid" :style="{ '--slots': result.total }">
      <span v-for="n in result.sprintCount" :key="n" class="sprint" :style="{ gridColumn: `${(n - 1) * 2 + 2} / span 2` }">{{ sprintLabel(n) }}</span>

      <template v-for="(lane, i) in result.lanes" :key="lane.roadmap.id">
        <span class="label" :style="{ gridRow: i + 2 }"><i :style="{ background: lane.colour }" />{{ lane.roadmap.name }}</span>
        <span class="track" :style="{ gridRow: i + 2 }" />
        <span v-for="run in laneRuns[i]" :key="run.start" class="run" :style="{ ...pos(run, i + 2), background: lane.colour }" />
      </template>

      <span class="label label--total" :style="{ gridRow: result.lanes.length + 2 }">Together</span>
      <span
        v-for="run in combined"
        :key="`c${run.start}`"
        class="run combined"
        :class="`is-${run.key}`"
        :style="pos(run, result.lanes.length + 2)"
        :title="`${rangeLabel(run.start, run.span)}: ${run.key === 'gap' ? 'nothing planned' : run.key === 'many' ? 'both roadmaps running' : 'one roadmap running'}`"
      >
        <template v-if="run.key === 'gap'">Gap</template>
        <template v-else-if="run.key === 'many' && run.span > 1">Overlap</template>
      </span>
    </div>

    <ul v-if="clashText.length" class="clashes">
      <li v-for="line in clashText" :key="line"><i />{{ line }}</li>
    </ul>
    <p v-else class="clashes none">Nobody is booked on both roadmaps at once.</p>
  </div>
</template>

<style scoped lang="scss">
.grid {
  display: grid;
  grid-template-columns: 150px repeat(var(--slots), minmax(0, 1fr));
  grid-auto-rows: 28px;
  row-gap: 6px;

  .compact & {
    grid-template-columns: 96px repeat(var(--slots), minmax(0, 1fr));
    grid-auto-rows: 24px;
  }
}

.sprint {
  grid-row: 1;
  align-self: center;
  font-size: 11px;
  font-weight: 500;
  color: var(--text-3);
  text-align: center;
}

.label {
  grid-column: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  padding-right: 10px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-size: 12px;
  font-weight: 500;

  i {
    width: 9px;
    height: 9px;
    border-radius: 3px;
    flex-shrink: 0;
  }

  &--total {
    font-weight: 600;
  }
}

.track {
  grid-column: 2 / -1;
  border-radius: 6px;
  background: var(--surface-2);
  box-shadow: inset 0 0 0 1px var(--line);
}

.run {
  margin: 0 1px;
  border-radius: 6px;
  transition: all 0.25s ease;
}

.combined {
  display: grid;
  place-items: center;
  overflow: hidden;
  font-size: 10px;
  font-weight: 600;
  white-space: nowrap;

  &.is-gap {
    background: repeating-linear-gradient(135deg, #fbe2a8 0 6px, #fff6e0 6px 12px);
    box-shadow: inset 0 0 0 1px #f2c25c;
    color: #7a5300;
  }

  &.is-one {
    background: var(--iris-soft);
  }

  &.is-many {
    background: var(--iris);
    color: #fff;
  }
}

.clashes {
  list-style: none;
  margin: 14px 0 0;
  padding: 0;
  font-size: 13px;
  color: var(--text-2);

  li {
    display: flex;
    align-items: center;
    gap: 8px;

    & + li {
      margin-top: 4px;
    }
  }

  i {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--danger);
    flex-shrink: 0;
  }

  &.none {
    color: var(--text-3);
  }
}
</style>
